import type {DraggableAttributes} from '@dnd-kit/core';
import type {SyntheticListenerMap} from '@dnd-kit/core/dist/hooks/utilities';
import {IconButton} from 'platform/components';
import {Box, HStack, Icon, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {isEmpty} from 'ramda';

import {RelativeFilterResponseBody} from '@dms/api/core';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getPresetTags} from '../../RelativeFiltersPresetEditor/utils/getPresetTags';

interface RelativeFilterPresetListItemProps extends TestIdProps {
  preset: RelativeFilterResponseBody;
  isSortable: boolean;
  dragHandleAttributes?: DraggableAttributes;
  dragHandleListeners?: SyntheticListenerMap;
  onOpen: () => void;
  onEdit: () => void;
}

export function RelativeFilterPresetListItem(props: RelativeFilterPresetListItemProps) {
  const {preset, isSortable, dragHandleAttributes, dragHandleListeners, onOpen, onEdit} = props;
  const tags = getPresetTags(preset.config);

  return (
    <RowRoot>
      <OpenButton type="button" onClick={onOpen} data-testid={suffixTestId('open', props)}>
        <HStack spacing={2} align="center">
          <DragHandleSlot>
            {isSortable ? (
              <DragHandle
                {...dragHandleAttributes}
                {...dragHandleListeners}
                onClick={(event) => event.stopPropagation()}
              >
                <Icon value="action/drag_indicator" color="palettes.neutral.60.100" size={4} />
              </DragHandle>
            ) : null}
          </DragHandleSlot>
          <Box flexGrow={1} minWidth={0}>
            <VStack spacing={1}>
              <Text size="small" alternative>
                {preset.name}
              </Text>
              {!isEmpty(tags) ? (
                <Text size="xSmall" color="secondary" noWrap>
                  {tags.map((tag) => tag.label).join(', ')}
                </Text>
              ) : null}
            </VStack>
          </Box>
        </HStack>
      </OpenButton>
      <IconButton
        icon="image/edit"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onEdit();
        }}
        data-testid={suffixTestId('edit', props)}
      />
    </RowRoot>
  );
}

const RowRoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
  padding: ${({theme}) => theme.getSize(1)};
  border-radius: ${({theme}) => theme.radii.small};

  &:hover,
  &:focus-within {
    background-color: ${({theme}) => theme.colors.palettes.blue[10][100]};
  }
`;

const OpenButton = styled.button`
  display: block;
  flex: 1;
  min-width: 0;
  padding: ${({theme}) => theme.getSize(1)};
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: none;
  }
`;

const DragHandleSlot = styled.div`
  width: ${({theme}) => theme.getSize(4)};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  opacity: 0;

  ${RowRoot}:hover &,
  ${RowRoot}:focus-within & {
    opacity: 1;
  }
`;
